import { useState } from 'react'

import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'

import { RoutesEnum } from 'src/Router/types.ts'
import { useGetOfferByIdQuery } from 'src/Redux/api/offersApi'
import {
  useCreateContractMutation,
  useGenerateContractDocumentMutation,
} from 'src/Redux/api/contractsApi'
import { useUpdateClientMutation } from 'src/Redux/api/clientsApi'
import { AddClientBody } from 'src/Redux/api/clientsApi'

export interface EquipmentSelection {
  equipmentId: number | null
  workType: string
  shiftsCount: number | null
  minHoursPerShift: number
}

export interface ContractFormData {
  contractDate: Date | null
  endDate: Date | null
  reportingPeriodName: string
  reportingPeriodDescription: string
  foodRule: string
  accommodationRule: string
  fuelRule: string
  rentPeriod: number
  equipmentDeliveryType: 'date' | 'days'
  equipmentDeliveryDate: Date | null
  equipmentDeliveryDays: number | null
  prepaymentNoticeDays: number | null
  dailyFoodCostPerEmployee: number | null
}

interface UseCreateContractReturn {
  control: ReturnType<typeof useForm<ContractFormData>>['control']
  errors: ReturnType<typeof useForm<ContractFormData>>['formState']['errors']
  offer?: ReturnType<typeof useGetOfferByIdQuery>['data']
  handleSubmit: ReturnType<typeof useForm<ContractFormData>>['handleSubmit']
  onSubmit: (data: ContractFormData) => Promise<void>
  handleCancel: () => void
  setValue: ReturnType<typeof useForm<ContractFormData>>['setValue']
  selectedEquipments: Record<number, EquipmentSelection[]>
  onEquipmentSelect: (
    offerModelId: number,
    index: number,
    equipmentId: number | null,
  ) => void
  onWorkTypeChange: (
    offerModelId: number,
    index: number,
    workType: string,
  ) => void
  onShiftsCountChange: (
    offerModelId: number,
    index: number,
    shiftsCount: number | null,
  ) => void
  onMinHoursPerShiftChange: (
    offerModelId: number,
    index: number,
    minHoursPerShift: number,
  ) => void
  isGeneratingDocument: boolean
  isEditClientModalOpen: boolean
  openEditClientModal: () => void
  closeEditClientModal: () => void
  handleUpdateClient: (data: AddClientBody) => void
}

export const useCreateContract = (): UseCreateContractReturn => {
  const navigate = useNavigate()
  const { offerId } = useParams<{ offerId: string }>()
  const [selectedEquipments, setSelectedEquipments] = useState<
    Record<number, EquipmentSelection[]>
  >({})
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false)
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false)

  const { data: offer, refetch: refetchOffer } = useGetOfferByIdQuery(
    Number(offerId),
    {
      skip: !offerId,
    },
  )

  const [createContract] = useCreateContractMutation()
  const [generateContractDocument] = useGenerateContractDocumentMutation()
  const [updateClient] = useUpdateClientMutation()

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ContractFormData>({
    defaultValues: {
      contractDate: new Date(),
      endDate: dayjs().add(1, 'year').toDate(),
      reportingPeriodName: 'half_month',
      reportingPeriodDescription: 'с 1 по 15 число и с 16 до конца месяца',
      foodRule: 'tenant_tenant',
      accommodationRule: 'tenant_tenant',
      fuelRule: 'tenant_tenant',
      rentPeriod: 1,
      equipmentDeliveryType: 'days',
      equipmentDeliveryDate: null,
      equipmentDeliveryDays: null,
      prepaymentNoticeDays: null,
      dailyFoodCostPerEmployee: null,
    },
  })

  const onEquipmentSelect = (
    offerModelId: number,
    index: number,
    equipmentId: number | null,
  ): void => {
    setSelectedEquipments((prev) => {
      const current = prev[offerModelId] || []
      const updated = [...current]
      updated[index] = {
        equipmentId,
        workType: updated[index]?.workType || '',
        shiftsCount: updated[index]?.shiftsCount || null,
        minHoursPerShift: updated[index]?.minHoursPerShift || 10,
      }
      return { ...prev, [offerModelId]: updated }
    })
  }

  const onWorkTypeChange = (
    offerModelId: number,
    index: number,
    workType: string,
  ): void => {
    setSelectedEquipments((prev) => {
      const current = prev[offerModelId] || []
      const updated = [...current]
      updated[index] = {
        equipmentId: updated[index]?.equipmentId || null,
        workType,
        shiftsCount: updated[index]?.shiftsCount || null,
        minHoursPerShift: updated[index]?.minHoursPerShift || 10,
      }
      return { ...prev, [offerModelId]: updated }
    })
  }

  const onShiftsCountChange = (
    offerModelId: number,
    index: number,
    shiftsCount: number | null,
  ): void => {
    setSelectedEquipments((prev) => {
      const current = prev[offerModelId] || []
      const updated = [...current]
      updated[index] = {
        equipmentId: updated[index]?.equipmentId || null,
        workType: updated[index]?.workType || '',
        shiftsCount,
        minHoursPerShift: updated[index]?.minHoursPerShift || 10,
      }
      return { ...prev, [offerModelId]: updated }
    })
  }

  const onMinHoursPerShiftChange = (
    offerModelId: number,
    index: number,
    minHoursPerShift: number,
  ): void => {
    setSelectedEquipments((prev) => {
      const current = prev[offerModelId] || []
      const updated = [...current]
      updated[index] = {
        equipmentId: updated[index]?.equipmentId || null,
        workType: updated[index]?.workType || '',
        shiftsCount: updated[index]?.shiftsCount || null,
        minHoursPerShift,
      }
      return { ...prev, [offerModelId]: updated }
    })
  }

  const onSubmit = async (data: ContractFormData): Promise<void> => {
    try {
      // Собираем все выбранные equipments с их видом работ, количеством смен и минимальным количеством часов
      const equipments: Array<{
        equipmentId: number
        workType: string
        shiftsCount?: number
        minHoursPerShift?: number
      }> = []
      Object.values(selectedEquipments).forEach((equipmentList) => {
        equipmentList.forEach((equipment) => {
          if (
            equipment?.equipmentId !== null &&
            equipment?.equipmentId !== undefined
          ) {
            equipments.push({
              equipmentId: equipment.equipmentId,
              workType: equipment.workType || '',
              shiftsCount: equipment.shiftsCount || undefined,
              minHoursPerShift: equipment.minHoursPerShift || 10,
            })
          }
        })
      })

      const contractData = {
        offerId: Number(offerId),
        contractDate: data.contractDate
          ? dayjs(data.contractDate).format('YYYY-MM-DD')
          : undefined,
        endDate: data.endDate
          ? dayjs(data.endDate).format('YYYY-MM-DD')
          : undefined,
        reportingPeriodName: data.reportingPeriodName,
        reportingPeriodDescription: data.reportingPeriodDescription,
        foodRule: data.foodRule,
        accommodationRule: data.accommodationRule,
        fuelRule: data.fuelRule,
        rentPeriod: data.rentPeriod,
        equipmentDeliveryDate:
          data.equipmentDeliveryType === 'date' && data.equipmentDeliveryDate
            ? dayjs(data.equipmentDeliveryDate).format('YYYY-MM-DD')
            : undefined,
        equipmentDeliveryDays:
          data.equipmentDeliveryType === 'days' && data.equipmentDeliveryDays
            ? data.equipmentDeliveryDays
            : undefined,
        prepaymentNoticeDays: data.prepaymentNoticeDays ?? undefined,
        dailyFoodCostPerEmployee: data.dailyFoodCostPerEmployee ?? undefined,
        equipments: equipments.length > 0 ? equipments : undefined,
      }

      // Создаем договор
      const createdContract = await createContract(contractData).unwrap()

      // Автоматически генерируем Google документ для созданного договора
      try {
        setIsGeneratingDocument(true)
        await generateContractDocument(createdContract.data.id).unwrap()
      } catch (docError) {
        console.error('Failed to generate Google document:', docError)
        // Продолжаем навигацию, даже если генерация документа не удалась
      } finally {
        setIsGeneratingDocument(false)
      }

      navigate(RoutesEnum.CONTRACTS)
    } catch (error) {
      console.error('Failed to create contract:', error)
    }
  }

  const handleCancel = (): void => {
    navigate(RoutesEnum.CONTRACTS)
  }

  const openEditClientModal = (): void => {
    setIsEditClientModalOpen(true)
  }

  const closeEditClientModal = (): void => {
    setIsEditClientModalOpen(false)
  }

  const handleUpdateClient = async (data: AddClientBody): Promise<void> => {
    if (!offer?.clientId) return
    try {
      await updateClient({ id: offer.clientId, data }).unwrap()
      // Обновляем данные offer, чтобы отобразить актуальную информацию о клиенте
      await refetchOffer()
      closeEditClientModal()
    } catch (error) {
      console.error('Failed to update client:', error)
    }
  }

  return {
    control,
    errors,
    offer,
    handleSubmit,
    onSubmit,
    handleCancel,
    setValue,
    selectedEquipments,
    onEquipmentSelect,
    onWorkTypeChange,
    onShiftsCountChange,
    onMinHoursPerShiftChange,
    isGeneratingDocument,
    isEditClientModalOpen,
    openEditClientModal,
    closeEditClientModal,
    handleUpdateClient,
  }
}
